import { useState, useCallback } from "react";

type JState = "none" | "tender" | "swollen" | "both";
type Mode   = "tender" | "swollen";

interface JP { id: number; name: string; x: number; y: number; r: number; }

// ── 60-joint peripheral map for PsA (TJC68 / SJC66 subset) ───────────────────
// Includes all DAS28 joints PLUS: hips, ankles, hand DIPs, MTPs, foot PIPs
// (Omits temporomandibular / sternoclavicular / acromioclavicular for clarity)

const JOINTS_PSA: JP[] = [
  // Shoulders
  { id: 0,  name: "L Shoulder",   x: 72,  y: 100, r: 8 },
  { id: 1,  name: "R Shoulder",   x: 228, y: 100, r: 8 },
  // Elbows
  { id: 2,  name: "L Elbow",      x: 50,  y: 172, r: 8 },
  { id: 3,  name: "R Elbow",      x: 250, y: 172, r: 8 },
  // Wrists
  { id: 4,  name: "L Wrist",      x: 40,  y: 244, r: 8 },
  { id: 5,  name: "R Wrist",      x: 260, y: 244, r: 8 },
  // Left hand MCPs (thumb=1, 13 px apart)
  { id: 6,  name: "L MCP-1",      x: 10,  y: 270, r: 6 },
  { id: 7,  name: "L MCP-2",      x: 23,  y: 270, r: 6 },
  { id: 8,  name: "L MCP-3",      x: 36,  y: 270, r: 6 },
  { id: 9,  name: "L MCP-4",      x: 49,  y: 270, r: 6 },
  { id: 10, name: "L MCP-5",      x: 62,  y: 270, r: 6 },
  // Left hand PIPs
  { id: 11, name: "L PIP-1",      x: 10,  y: 300, r: 6 },
  { id: 12, name: "L PIP-2",      x: 23,  y: 300, r: 6 },
  { id: 13, name: "L PIP-3",      x: 36,  y: 300, r: 6 },
  { id: 14, name: "L PIP-4",      x: 49,  y: 300, r: 6 },
  { id: 15, name: "L PIP-5",      x: 62,  y: 300, r: 6 },
  // Left hand DIPs (2–5 only — no thumb DIP in TJC68)
  { id: 16, name: "L DIP-2",      x: 23,  y: 330, r: 5 },
  { id: 17, name: "L DIP-3",      x: 36,  y: 330, r: 5 },
  { id: 18, name: "L DIP-4",      x: 49,  y: 330, r: 5 },
  { id: 19, name: "L DIP-5",      x: 62,  y: 330, r: 5 },
  // Right hand MCPs (thumb=1 on right side)
  { id: 20, name: "R MCP-1",      x: 290, y: 270, r: 6 },
  { id: 21, name: "R MCP-2",      x: 277, y: 270, r: 6 },
  { id: 22, name: "R MCP-3",      x: 264, y: 270, r: 6 },
  { id: 23, name: "R MCP-4",      x: 251, y: 270, r: 6 },
  { id: 24, name: "R MCP-5",      x: 238, y: 270, r: 6 },
  // Right hand PIPs
  { id: 25, name: "R PIP-1",      x: 290, y: 300, r: 6 },
  { id: 26, name: "R PIP-2",      x: 277, y: 300, r: 6 },
  { id: 27, name: "R PIP-3",      x: 264, y: 300, r: 6 },
  { id: 28, name: "R PIP-4",      x: 251, y: 300, r: 6 },
  { id: 29, name: "R PIP-5",      x: 238, y: 300, r: 6 },
  // Right hand DIPs (2–5)
  { id: 30, name: "R DIP-2",      x: 277, y: 330, r: 5 },
  { id: 31, name: "R DIP-3",      x: 264, y: 330, r: 5 },
  { id: 32, name: "R DIP-4",      x: 251, y: 330, r: 5 },
  { id: 33, name: "R DIP-5",      x: 238, y: 330, r: 5 },
  // Hips
  { id: 34, name: "L Hip",        x: 104, y: 246, r: 8 },
  { id: 35, name: "R Hip",        x: 196, y: 246, r: 8 },
  // Knees
  { id: 36, name: "L Knee",       x: 108, y: 366, r: 9 },
  { id: 37, name: "R Knee",       x: 192, y: 366, r: 9 },
  // Ankles
  { id: 38, name: "L Ankle",      x: 110, y: 444, r: 8 },
  { id: 39, name: "R Ankle",      x: 190, y: 444, r: 8 },
  // Left foot MTPs (big toe=1, innermost)
  { id: 40, name: "L MTP-1",      x: 98,  y: 468, r: 5 },
  { id: 41, name: "L MTP-2",      x: 107, y: 465, r: 5 },
  { id: 42, name: "L MTP-3",      x: 115, y: 464, r: 5 },
  { id: 43, name: "L MTP-4",      x: 123, y: 465, r: 5 },
  { id: 44, name: "L MTP-5",      x: 130, y: 468, r: 5 },
  // Left foot PIPs (IP hallux + PIP 2–5)
  { id: 45, name: "L IP hallux",  x: 98,  y: 487, r: 5 },
  { id: 46, name: "L fPIP-2",     x: 107, y: 485, r: 5 },
  { id: 47, name: "L fPIP-3",     x: 115, y: 484, r: 5 },
  { id: 48, name: "L fPIP-4",     x: 123, y: 485, r: 5 },
  { id: 49, name: "L fPIP-5",     x: 130, y: 487, r: 5 },
  // Right foot MTPs (mirrored)
  { id: 50, name: "R MTP-1",      x: 202, y: 468, r: 5 },
  { id: 51, name: "R MTP-2",      x: 193, y: 465, r: 5 },
  { id: 52, name: "R MTP-3",      x: 185, y: 464, r: 5 },
  { id: 53, name: "R MTP-4",      x: 177, y: 465, r: 5 },
  { id: 54, name: "R MTP-5",      x: 170, y: 468, r: 5 },
  // Right foot PIPs
  { id: 55, name: "R IP hallux",  x: 202, y: 487, r: 5 },
  { id: 56, name: "R fPIP-2",     x: 193, y: 485, r: 5 },
  { id: 57, name: "R fPIP-3",     x: 185, y: 484, r: 5 },
  { id: 58, name: "R fPIP-4",     x: 177, y: 485, r: 5 },
  { id: 59, name: "R fPIP-5",     x: 170, y: 487, r: 5 },
];

const L_HAND_X = [10, 23, 36, 49, 62];
const R_HAND_X = [290, 277, 264, 251, 238];

function dotFill(s: JState): string {
  if (s === "none")    return "#f8fafc";
  if (s === "tender")  return "#fb923c";
  if (s === "swollen") return "#60a5fa";
  return "#a78bfa";
}
function dotStroke(s: JState): string {
  if (s === "none")    return "#94a3b8";
  if (s === "tender")  return "#ea580c";
  if (s === "swollen") return "#2563eb";
  return "#7c3aed";
}

export interface JointSelectorPsAProps {
  onChange: (tjc: number, sjc: number) => void;
  label?: string;
}

export function JointSelectorPsA({ onChange, label }: JointSelectorPsAProps) {
  const [mode, setMode]     = useState<Mode>("tender");
  const [states, setStates] = useState<JState[]>(Array(60).fill("none"));

  const toggle = useCallback((id: number) => {
    setStates((prev) => {
      const next = [...prev];
      const cur  = next[id];
      if (mode === "tender") {
        next[id] = cur === "none" ? "tender" : cur === "tender" ? "none" : cur === "swollen" ? "both" : "swollen";
      } else {
        next[id] = cur === "none" ? "swollen" : cur === "swollen" ? "none" : cur === "tender" ? "both" : "tender";
      }
      onChange(
        next.filter((s) => s === "tender" || s === "both").length,
        next.filter((s) => s === "swollen" || s === "both").length,
      );
      return next;
    });
  }, [mode, onChange]);

  const reset = () => { setStates(Array(60).fill("none")); onChange(0, 0); };

  const tjc = states.filter((s) => s === "tender" || s === "both").length;
  const sjc = states.filter((s) => s === "swollen" || s === "both").length;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            {label ?? "60-Joint Map"}
          </p>
          <p className="text-xs text-slate-400">Shoulders · Elbows · Wrists · MCPs · PIPs · DIPs · Hips · Knees · Ankles · MTPs · Foot PIPs</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
            <button onClick={() => setMode("tender")}
              className={`px-3 py-1.5 font-medium transition-colors ${mode === "tender" ? "bg-orange-500 text-white" : "bg-white text-slate-600 hover:bg-orange-50"}`}>
              ● Tender
            </button>
            <button onClick={() => setMode("swollen")}
              className={`px-3 py-1.5 font-medium transition-colors ${mode === "swollen" ? "bg-blue-500 text-white" : "bg-white text-slate-600 hover:bg-blue-50"}`}>
              ● Swollen
            </button>
          </div>
          <button onClick={reset}
            className="text-xs text-slate-400 hover:text-slate-700 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 transition-colors">
            Reset
          </button>
        </div>
      </div>

      <div className="flex justify-center">
        <svg viewBox="0 0 300 510" className="w-full max-w-[240px]" style={{ userSelect: "none" }}>

          {/* ── Decorative body ── */}
          <ellipse cx="150" cy="35" rx="20" ry="25" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1.5" />
          <line x1="150" y1="60" x2="150" y2="76" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="108" y1="76" x2="192" y2="76" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="108" y1="76" x2="72" y2="100" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="192" y1="76" x2="228" y2="100" stroke="#cbd5e1" strokeWidth="3" />
          <path d="M 108,76 L 106,226 L 194,226 L 192,76" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.5" />

          {/* Arms */}
          <line x1="72" y1="100" x2="50" y2="172" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="50" y1="172" x2="40" y2="244" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="228" y1="100" x2="250" y2="172" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="250" y1="172" x2="260" y2="244" stroke="#cbd5e1" strokeWidth="3" />

          {/* Left hand box — 3 rows: MCP / PIP / DIP */}
          <rect x="2" y="250" width="78" height="90" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1.5" />
          <text x="4" y="273" fontSize="4.5" fill="#94a3b8" fontWeight="600">MCP</text>
          <text x="4" y="303" fontSize="4.5" fill="#94a3b8" fontWeight="600">PIP</text>
          <text x="4" y="333" fontSize="4.5" fill="#94a3b8" fontWeight="600">DIP</text>
          {L_HAND_X.map((fx, i) => (
            <text key={i} x={fx} y="260" fontSize="4.5" fill="#94a3b8" textAnchor="middle">{i + 1}</text>
          ))}
          <text x="41" y="248" fontSize="7" fill="#64748b" fontWeight="700" textAnchor="middle">L</text>

          {/* Right hand box */}
          <rect x="220" y="250" width="78" height="90" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1.5" />
          <text x="222" y="273" fontSize="4.5" fill="#94a3b8" fontWeight="600">MCP</text>
          <text x="222" y="303" fontSize="4.5" fill="#94a3b8" fontWeight="600">PIP</text>
          <text x="222" y="333" fontSize="4.5" fill="#94a3b8" fontWeight="600">DIP</text>
          {R_HAND_X.map((fx, i) => (
            <text key={i} x={fx} y="260" fontSize="4.5" fill="#94a3b8" textAnchor="middle">{i + 1}</text>
          ))}
          <text x="259" y="248" fontSize="7" fill="#64748b" fontWeight="700" textAnchor="middle">R</text>

          {/* Pelvis → hips */}
          <line x1="106" y1="226" x2="104" y2="246" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="194" y1="226" x2="196" y2="246" stroke="#cbd5e1" strokeWidth="3" />

          {/* Legs (thigh → knee → shin → ankle) */}
          <line x1="104" y1="246" x2="108" y2="366" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="108" y1="366" x2="110" y2="444" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="196" y1="246" x2="192" y2="366" stroke="#cbd5e1" strokeWidth="3" />
          <line x1="192" y1="366" x2="190" y2="444" stroke="#cbd5e1" strokeWidth="3" />

          {/* Ankle → foot */}
          <line x1="110" y1="444" x2="114" y2="452" stroke="#cbd5e1" strokeWidth="2" />
          <line x1="190" y1="444" x2="186" y2="452" stroke="#cbd5e1" strokeWidth="2" />

          {/* Left foot box */}
          <rect x="88" y="452" width="52" height="50" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1.5" />
          <text x="90" y="471" fontSize="4.5" fill="#94a3b8" fontWeight="600">MTP</text>
          <text x="90" y="490" fontSize="4.5" fill="#94a3b8" fontWeight="600">PIP</text>
          {[98, 107, 115, 123, 130].map((fx, i) => (
            <text key={i} x={fx} y="461" fontSize="4" fill="#94a3b8" textAnchor="middle">{i + 1}</text>
          ))}
          <text x="114" y="450" fontSize="7" fill="#64748b" fontWeight="700" textAnchor="middle">L</text>

          {/* Right foot box */}
          <rect x="160" y="452" width="52" height="50" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1.5" />
          <text x="162" y="471" fontSize="4.5" fill="#94a3b8" fontWeight="600">MTP</text>
          <text x="162" y="490" fontSize="4.5" fill="#94a3b8" fontWeight="600">PIP</text>
          {[202, 193, 185, 177, 170].map((fx, i) => (
            <text key={i} x={fx} y="461" fontSize="4" fill="#94a3b8" textAnchor="middle">{i + 1}</text>
          ))}
          <text x="186" y="450" fontSize="7" fill="#64748b" fontWeight="700" textAnchor="middle">R</text>

          {/* L/R knee labels */}
          <text x="96"  y="370" fontSize="7" fill="#64748b" fontWeight="700" textAnchor="middle">L</text>
          <text x="204" y="370" fontSize="7" fill="#64748b" fontWeight="700" textAnchor="middle">R</text>

          {/* ── Joint dots ── */}
          {JOINTS_PSA.map((j) => {
            const s = states[j.id];
            return (
              <g key={j.id} onClick={() => toggle(j.id)} style={{ cursor: "pointer" }}>
                <circle cx={j.x} cy={j.y} r={j.r + 5} fill="transparent" />
                <circle cx={j.x} cy={j.y} r={j.r} fill={dotFill(s)} stroke={dotStroke(s)} strokeWidth="1.5" />
                {s !== "none" && (
                  <text x={j.x} y={j.y + 0.5}
                    fontSize={j.r > 6 ? "6.5" : "5"}
                    fill="white" textAnchor="middle" dominantBaseline="middle"
                    style={{ pointerEvents: "none" }}>
                    {s === "tender" ? "T" : s === "swollen" ? "S" : "B"}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Counters */}
      <div className="flex gap-3">
        <div className="flex-1 rounded-xl border-2 border-orange-200 bg-orange-50 py-2 text-center">
          <p className="text-xs text-orange-600 font-medium">Tender</p>
          <p className="text-2xl font-bold text-orange-600">{tjc}</p>
          <p className="text-xs text-orange-400">/ 60</p>
        </div>
        <div className="flex-1 rounded-xl border-2 border-blue-200 bg-blue-50 py-2 text-center">
          <p className="text-xs text-blue-600 font-medium">Swollen</p>
          <p className="text-2xl font-bold text-blue-600">{sjc}</p>
          <p className="text-xs text-blue-400">/ 60</p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-orange-400" /> Tender (T)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-400" /> Swollen (S)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-violet-400" /> Both (B)</span>
      </div>
    </div>
  );
}
